/**
 * Stylesheet of the exported GCC dashboard (WI 2461). Kept apart from
 * generateGccHtml.ts so the page markup and data wiring stay readable.
 *
 * The page sets the brand variables (--brand, --brand2, --night, --night2,
 * --night3, --nav-bg, --brand-on-dark, --brand-ink, --brand-soft, --brand2-soft)
 * from the project branding, through gccExecutivePaletteCss (@tn/shared). Chart series use
 * the fixed, validated categorical palette below (--c-ret, --c-gcc, --c-msp,
 * --c-ai), so the meaning of a colour never changes with a client's brand.
 *
 * Motion only runs when the page script adds .motion to <html> (it checks
 * prefers-reduced-motion first). Without it every element is drawn in its
 * final state, which is also what printing and the unit tests see.
 *
 * The :root tokens come from @tn/shared, so the GCC TN Analysis dashboards
 * (injectGccExecutiveLayer in @tn/core) share the same look.
 */
import { GCC_EXECUTIVE_TOKENS } from '@tn/shared'

export const GCC_EXPORT_CSS = `
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{${GCC_EXECUTIVE_TOKENS}}
html{-webkit-text-size-adjust:100%}
body{font-family:'Graphik','Inter','Segoe UI Variable Text','Segoe UI',-apple-system,BlinkMacSystemFont,Roboto,sans-serif;background:var(--bg);color:var(--ink);font-size:14px;line-height:1.45;min-height:100vh;display:flex;flex-direction:column;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale}
h1,h2,h3{font-weight:600;letter-spacing:-.02em}
button{font:inherit}

/* ── Hero ─────────────────────────────────────────────── */
.hero{position:relative;overflow:hidden;color:#fff;background:linear-gradient(120deg,var(--night) 0%,var(--night2) 58%,var(--night3) 100%);padding:0 40px 36px;isolation:isolate}
.hero::before,.hero::after{content:'';position:absolute;border-radius:50%;filter:blur(60px);z-index:-1;pointer-events:none}
.hero::before{width:620px;height:620px;right:-140px;top:-300px;background:radial-gradient(circle,var(--brand) 0%,transparent 66%);opacity:.55}
.hero::after{width:520px;height:520px;left:22%;bottom:-380px;background:radial-gradient(circle,var(--brand2) 0%,transparent 66%);opacity:.45}
.hero-grid{position:absolute;inset:0;z-index:-1;pointer-events:none;opacity:.5;background-image:linear-gradient(rgba(255,255,255,.045) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.045) 1px,transparent 1px);background-size:56px 56px;-webkit-mask-image:linear-gradient(180deg,#000 0%,transparent 85%);mask-image:linear-gradient(180deg,#000 0%,transparent 85%)}
.hero-top{display:flex;align-items:center;gap:28px;padding:18px 0 0;flex-wrap:wrap}
.wordmark{display:flex;flex-direction:column;line-height:1;min-width:120px}
.wordmark b{font-size:17px;font-weight:700;letter-spacing:.06em;white-space:nowrap}
.wordmark span{font-size:9.5px;font-weight:600;letter-spacing:.32em;text-transform:uppercase;opacity:.7;margin-top:5px}
.hero-meta{margin-left:auto;display:flex;gap:10px;flex-wrap:wrap}
.chip{display:flex;flex-direction:column;gap:2px;padding:7px 14px;border-radius:10px;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.12);min-width:120px;-webkit-backdrop-filter:blur(6px);backdrop-filter:blur(6px)}
.chip small{font-size:9.5px;letter-spacing:.14em;text-transform:uppercase;opacity:.6}
.chip strong{font-size:12.5px;font-weight:600}
.hero-body{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:40px;align-items:end;padding-top:34px}
.hero-eyebrow{font-size:11px;letter-spacing:.2em;text-transform:uppercase;color:rgba(255,255,255,.62);margin-bottom:12px}
.hero-title{font-size:40px;line-height:1.08;font-weight:600;letter-spacing:-.025em;max-width:880px}
.hero-sub{font-size:16px;color:rgba(255,255,255,.78);margin-top:12px;max-width:760px}
#hero-copy{min-height:142px;display:flex;align-items:flex-end}
.hero-swap{animation:none}
.motion .hero-swap{animation:heroIn .6s var(--ease) both}
.hero-headline{text-align:right;padding-left:28px;border-left:1px solid rgba(255,255,255,.16)}
.hero-headline .hh-num{font-size:56px;font-weight:600;letter-spacing:-.03em;line-height:1}
.hero-headline .hh-lbl{font-size:12px;color:rgba(255,255,255,.7);margin-top:8px;max-width:220px;margin-left:auto}
.hero-headline .hh-sub{font-size:12px;color:rgba(255,255,255,.5);margin-top:4px}
.tagline{font-size:10.5px;font-weight:600;letter-spacing:.24em;text-transform:uppercase;line-height:1.75;color:rgba(255,255,255,.5);margin-top:14px;text-align:right}
.motion .hero::before{animation:drift1 22s ease-in-out infinite alternate}
.motion .hero::after{animation:drift2 26s ease-in-out infinite alternate}

/* ── Tabs ─────────────────────────────────────────────── */
/* One row that scrolls sideways (WI 2485). A fade and a chevron appear only on a
   side with tabs out of view; the page script sets .fade-l/.fade-r and .on. */
.tab-nav{position:sticky;top:0;z-index:20;background:var(--nav-bg);-webkit-backdrop-filter:saturate(1.4) blur(12px);backdrop-filter:saturate(1.4) blur(12px);border-bottom:1px solid rgba(255,255,255,.08);padding:0 40px}
.tab-bar{display:flex;align-items:stretch}
.tab-rail{position:relative;flex:1;min-width:0}
.tab-nav-inner{position:relative;display:flex;gap:2px;overflow-x:auto;scrollbar-width:none;counter-reset:story;--fl:0px;--fr:0px;-webkit-mask-image:linear-gradient(90deg,transparent 0,#000 var(--fl),#000 calc(100% - var(--fr)),transparent 100%);mask-image:linear-gradient(90deg,transparent 0,#000 var(--fl),#000 calc(100% - var(--fr)),transparent 100%)}
.tab-nav-inner::-webkit-scrollbar{display:none}
.tab-nav-inner.fade-l{--fl:56px}
.tab-nav-inner.fade-r{--fr:56px}
.motion .tab-nav-inner{scroll-behavior:smooth}
.tab-arrow{position:absolute;top:50%;transform:translateY(-50%);z-index:2;width:28px;height:28px;border-radius:50%;border:1px solid rgba(255,255,255,.18);background:rgba(255,255,255,.08);color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:14px;line-height:1;opacity:0;pointer-events:none;transition:opacity .2s ease,background .2s ease}
.tab-arrow:hover{background:rgba(255,255,255,.18)}
.tab-arrow.l{left:-6px}
.tab-arrow.r{right:-6px}
.tab-arrow.on{opacity:1;pointer-events:auto}
.tab-btn{position:relative;flex:none;border:none;cursor:pointer;padding:15px 16px 14px;font-size:13px;font-weight:500;color:rgba(255,255,255,.62);background:transparent;white-space:nowrap;transition:color .2s ease}
.tab-btn.story::before{counter-increment:story;content:'0' counter(story);font-size:10.5px;font-weight:600;letter-spacing:.06em;margin-right:8px;color:rgba(255,255,255,.4);transition:color .2s ease}
.tab-btn:hover{color:#fff}
.tab-btn.active{color:#fff;font-weight:600}
.tab-btn.active::before{color:var(--brand-on-dark)}
.tab-btn:focus-visible{outline:2px solid #fff;outline-offset:-4px;border-radius:6px}
.tab-ind{position:absolute;bottom:0;left:0;height:2px;width:0;border-radius:2px 2px 0 0;background:linear-gradient(90deg,var(--brand),var(--brand-on-dark));box-shadow:0 0 12px var(--brand)}
.motion .tab-ind{transition:left .45s var(--ease),width .45s var(--ease)}
/* Appendix menu (WI 2881): outside the scrolling row, so the dropdown is never clipped */
.appx{position:relative;flex:none;display:flex;margin-left:14px;padding-left:10px;border-left:1px solid rgba(255,255,255,.16)}
.appx[hidden]{display:none}
.appx-btn{position:relative;display:inline-flex;align-items:center;gap:7px;border:none;cursor:pointer;padding:15px 14px 14px;font-size:13px;font-weight:500;color:rgba(255,255,255,.62);background:transparent;white-space:nowrap;transition:color .2s ease}
.appx-btn:hover,.appx-btn[aria-expanded="true"]{color:#fff}
.appx-btn.active{color:#fff;font-weight:600}
.appx-btn.active::after{content:'';position:absolute;left:10px;right:10px;bottom:0;height:2px;border-radius:2px 2px 0 0;background:linear-gradient(90deg,var(--brand),var(--brand-on-dark));box-shadow:0 0 12px var(--brand)}
.appx-btn:focus-visible{outline:2px solid #fff;outline-offset:-4px;border-radius:6px}
.appx-chev{font-size:9px;opacity:.75;transition:transform .2s ease}
.appx-btn[aria-expanded="true"] .appx-chev{transform:rotate(180deg)}
.appx-menu{position:absolute;right:0;top:calc(100% + 6px);z-index:30;min-width:230px;display:flex;flex-direction:column;gap:1px;padding:6px;background:var(--surface);border:1px solid var(--line);border-radius:var(--r-md);box-shadow:var(--sh-2)}
.appx-menu[hidden]{display:none}
.appx-item{text-align:left;border:none;background:transparent;border-radius:8px;padding:9px 12px;font-size:13px;color:var(--ink2);cursor:pointer;white-space:nowrap}
.appx-item:hover,.appx-item:focus{background:var(--line2);color:var(--ink);outline:none}
.appx-item:focus-visible{box-shadow:inset 0 0 0 2px var(--brand)}
.appx-item.active{color:var(--ink);font-weight:600;background:linear-gradient(90deg,var(--brand-soft),var(--brand2-soft))}

/* ── Panels & layout ──────────────────────────────────── */
main{flex:1}
.tab-pnl{display:none;padding:28px 40px 40px;max-width:1680px;margin:0 auto}
.tab-pnl.active{display:block}
.sec{margin-top:28px}
.sec:first-child{margin-top:0}
.sec-head{display:flex;align-items:flex-end;justify-content:space-between;gap:16px;margin-bottom:14px}
.sec-title{font-size:19px;font-weight:600;letter-spacing:-.015em}
.sec-sub{font-size:13px;color:var(--ink2);margin-top:3px}
.card{background:var(--surface);border-radius:var(--r-lg);border:1px solid var(--line);box-shadow:var(--sh-1);padding:24px}
.card-title{font-size:15px;font-weight:600;letter-spacing:-.01em}
.card-sub{font-size:12px;color:var(--muted);margin-top:2px}
.grid2{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:20px}
.grid3{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:20px}
.lift{transition:transform .35s var(--ease),box-shadow .35s var(--ease),border-color .35s ease}
.lift:hover{transform:translateY(-3px);box-shadow:var(--sh-2);border-color:#D9DBE8}
.note{font-size:12px;color:var(--muted);line-height:1.55}
.empty{padding:28px;text-align:center;color:var(--muted);font-size:13px}

/* Reveal: sections rise in, staggered by --d */
.rv{transition:opacity .7s var(--ease) var(--d,0s),transform .7s var(--ease) var(--d,0s)}
.motion .tab-pnl:not(.in) .rv{opacity:0;transform:translateY(14px)}

/* ── States: Today → Tomorrow → End state ─────────────── */
.states{display:grid;grid-auto-flow:column;grid-auto-columns:minmax(0,1fr);gap:28px;position:relative}
.state{position:relative;background:var(--surface);border:1px solid var(--line);border-radius:var(--r-lg);box-shadow:var(--sh-1);padding:22px 22px 20px;overflow:hidden}
.state::before{content:'';position:absolute;left:0;right:0;top:0;height:3px;background:var(--st,var(--c-ret))}
.state+.state::after{content:'';position:absolute;left:-21px;top:50%;width:12px;height:12px;border-top:1.5px solid var(--muted);border-right:1.5px solid var(--muted);transform:translateY(-50%) rotate(45deg)}
.states .state{overflow:visible}
.st-name{font-size:20px;font-weight:600;letter-spacing:-.015em}
.st-when{font-size:12px;color:var(--muted);margin-top:2px}
.st-stats{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin:18px 0 14px;padding:14px 0;border-top:1px solid var(--line2);border-bottom:1px solid var(--line2)}
.stat .v{font-size:24px;font-weight:600;letter-spacing:-.02em;line-height:1.1}
.stat .l{font-size:11px;color:var(--muted);margin-top:3px}
.stat .d{font-size:11.5px;font-weight:600;margin-top:4px;color:var(--good)}
.stat .d.up{color:var(--bad)}
.stat .d.flat{color:var(--muted);font-weight:500}
.st-chart{display:flex;align-items:center;gap:18px}
.donut{flex:none;width:136px;height:136px}
.donut .seg{fill:none;stroke-width:15;transition:stroke-dasharray 1.1s var(--ease) var(--d,0s),opacity .2s ease}
.motion .tab-pnl:not(.in) .donut .seg{stroke-dasharray:0 400}
.donut .track{fill:none;stroke:var(--line2);stroke-width:15}
.donut .c-num{font-size:24px;font-weight:600;fill:var(--ink);letter-spacing:-.02em}
.donut .c-lbl{font-size:10px;fill:var(--muted);letter-spacing:.1em;text-transform:uppercase}
.donut .seg:hover,.donut .seg:focus{opacity:.8;outline:none}
.legend{list-style:none;display:flex;flex-direction:column;gap:7px;flex:1;min-width:0}
.legend li{display:grid;grid-template-columns:10px minmax(0,1fr) auto auto;gap:8px;align-items:center;font-size:12px;color:var(--ink2)}
.legend .sw{width:10px;height:10px;border-radius:3px}
.legend .lv{font-weight:600;color:var(--ink);font-variant-numeric:tabular-nums;text-align:right}
.legend .lp{color:var(--muted);font-variant-numeric:tabular-nums;min-width:36px;text-align:right}
.legend-row{display:flex;flex-wrap:wrap;gap:6px 18px;font-size:12px;color:var(--ink2)}
.legend-row span{display:inline-flex;align-items:center;gap:7px}
.legend-row i{width:10px;height:10px;border-radius:3px;display:inline-block}

/* ── Population value case cards ──────────────────────── */
.vc-cards{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:20px}
.vc-card{position:relative;background:var(--surface);border:1px solid var(--line);border-radius:var(--r-lg);box-shadow:var(--sh-1);padding:20px 22px;overflow:hidden}
.vc-card::before{content:'';position:absolute;top:0;left:0;bottom:0;width:3px}
.vc-card-gcc::before{background:var(--c-gcc)}
.vc-card-ret::before{background:var(--c-ret)}
.vc-card-comb::before{background:linear-gradient(180deg,var(--brand),var(--c-gcc))}
.vc-card-name{font-size:10.5px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:var(--muted);margin-bottom:10px}
.vc-big{font-size:30px;font-weight:600;letter-spacing:-.02em;line-height:1}
.vc-big-lbl{font-size:11.5px;color:var(--muted);margin:4px 0 12px}
.vc-saving{font-size:15px;font-weight:600;color:var(--good);margin-bottom:8px}
.vc-row{display:flex;justify-content:space-between;gap:12px;font-size:12px;color:var(--ink2);padding:4px 0;border-top:1px solid var(--line2)}
.vc-row-val{font-weight:600;color:var(--ink);font-variant-numeric:tabular-nums}

/* ── Value unlock strip ───────────────────────────────── */
.unlock{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));background:var(--surface);border:1px solid var(--line);border-radius:var(--r-lg);box-shadow:var(--sh-1);overflow:hidden}
.unlock>div{padding:20px 22px;border-left:1px solid var(--line2);position:relative}
.unlock>div:first-child{border-left:none}
.unlock .u-num{font-size:30px;font-weight:600;letter-spacing:-.025em;line-height:1;background:linear-gradient(90deg,var(--ink),var(--brand));-webkit-background-clip:text;background-clip:text;color:transparent}
.unlock .u-lbl{font-size:13px;font-weight:600;margin-top:10px}
.unlock .u-sub{font-size:12px;color:var(--muted);margin-top:3px;line-height:1.45}

/* ── Intro (WI 2881) ──────────────────────────────────── */
.intro-head{position:relative;overflow:hidden;padding:28px 30px}
.intro-head::before{content:'';position:absolute;left:0;top:0;bottom:0;width:3px;background:linear-gradient(180deg,var(--brand),var(--c-gcc))}
.intro-k{font-size:10.5px;font-weight:600;letter-spacing:.16em;text-transform:uppercase;color:var(--muted)}
.intro-client{font-size:28px;margin-top:6px}
.intro-purpose{font-size:15px;color:var(--ink2);margin-top:6px;max-width:820px}
.intro-cards{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:20px}
.intro-card{width:100%;height:100%;display:flex;flex-direction:column;gap:6px;text-align:left;cursor:pointer;font:inherit;color:inherit;background:var(--surface);border:1px solid var(--line);border-radius:var(--r-lg);box-shadow:var(--sh-1);padding:20px 22px}
.intro-card:focus-visible{outline:2px solid var(--brand);outline-offset:2px}
.ic-n{font-size:10.5px;font-weight:600;letter-spacing:.08em;color:var(--brand-ink)}
.ic-t{font-size:16px;font-weight:600;letter-spacing:-.01em}
.ic-d{font-size:12.5px;color:var(--ink2);line-height:1.5}
.ic-f{margin-top:auto;padding-top:10px;border-top:1px solid var(--line2);font-size:12px;color:var(--muted)}
.ic-f b{font-size:18px;font-weight:600;color:var(--ink);letter-spacing:-.015em}

/* ── Method timeline ──────────────────────────────────── */
.ov-title{font-size:15px;font-weight:600}
.ov-sub{font-size:12.5px;color:var(--muted);margin:3px 0 20px}
.step-list{list-style:none;display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:0;position:relative}
.step-list::before{content:'';position:absolute;left:18px;right:18px;top:17px;height:1px;background:linear-gradient(90deg,var(--c-ret),var(--c-gcc),var(--brand))}
.step-item{position:relative;padding-right:22px}
.step-circle{position:relative;width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:600;font-size:13px;background:var(--surface);border:1.5px solid var(--sc,var(--brand));color:var(--ink);margin-bottom:14px}
.step-title{font-size:13px;font-weight:600;margin-bottom:5px}
.step-desc{font-size:12px;color:var(--ink2);line-height:1.55}

/* ── Levers ───────────────────────────────────────────── */
.levers{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:0;background:var(--surface);border:1px solid var(--line);border-radius:var(--r-lg);box-shadow:var(--sh-1);overflow:hidden}
.lever{padding:22px 24px;border-left:1px solid var(--line2);position:relative}
.lever:first-child{border-left:none}
.lever::before{content:'';position:absolute;left:0;right:0;top:0;height:3px;background:var(--lc)}
.lv-k{font-size:10.5px;letter-spacing:.16em;text-transform:uppercase;color:var(--muted);font-weight:600}
.lv-t{font-size:17px;font-weight:600;letter-spacing:-.015em;margin-top:6px}
.lv-band{font-size:12px;color:var(--muted)}
.lv-figs{display:grid;grid-template-columns:1fr 1fr;gap:10px 16px;margin:16px 0 14px}
.lv-figs .v{font-size:19px;font-weight:600;letter-spacing:-.015em}
.lv-figs .l{font-size:11px;color:var(--muted)}
.lv-caps{list-style:none;border-top:1px solid var(--line2);padding-top:10px;display:flex;flex-direction:column;gap:6px}
.lv-caps li{display:flex;justify-content:space-between;gap:12px;font-size:12px;color:var(--ink2)}
.lv-caps li b{font-weight:600;color:var(--ink);font-variant-numeric:tabular-nums;white-space:nowrap}
.callout{display:inline-flex;flex-direction:column;align-items:flex-end;padding:8px 14px;border-radius:10px;border:1px solid var(--line);background:linear-gradient(135deg,#FBF7FF,#F3F8FF)}
.callout b{font-size:24px;font-weight:600;letter-spacing:-.02em;line-height:1.05}
.callout>span{font-size:11px;color:var(--muted)}
.chart{width:100%;height:auto;display:block;overflow:visible}
.chart text{font-family:inherit}
.chart .ax{stroke:var(--line);stroke-width:1}
.chart .grid{stroke:var(--line2);stroke-width:1}
.chart .tick{font-size:11px;fill:var(--muted)}
.chart .xl{font-size:12px;fill:var(--ink2);font-weight:500}
.chart .xs{font-size:10.5px;fill:var(--muted)}
.chart .val{font-size:12px;font-weight:600;fill:var(--ink)}
.chart .val-s{font-size:11px;font-weight:600;fill:var(--ink2)}
.chart .conn{stroke:#C9CCDA;stroke-width:1}
.bar{transform-box:fill-box;transform-origin:50% 100%;transition:transform 1s var(--ease) var(--d,0s),opacity .2s ease}
.hbar{transform-box:fill-box;transform-origin:0 50%;transition:transform 1s var(--ease) var(--d,0s),opacity .2s ease}
.bar-down{transform-origin:50% 0}
.motion .tab-pnl:not(.in) .bar{transform:scaleY(0)}
.motion .tab-pnl:not(.in) .hbar{transform:scaleX(0)}
.bar:hover,.bar:focus,.hbar:hover,.hbar:focus{opacity:.82;outline:none}
.fade{transition:opacity .9s ease var(--d,0s)}
.motion .tab-pnl:not(.in) .fade{opacity:0}
.fn-bars{display:flex;flex-direction:column;gap:10px}
.fnb{display:grid;grid-template-columns:minmax(120px,190px) minmax(0,1fr) 84px;gap:14px;align-items:center;font-size:12.5px}
.fnb .n{color:var(--ink2);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.fnb .t{font-weight:600;text-align:right;font-variant-numeric:tabular-nums}
.fnb .track{display:flex;gap:2px;height:10px}
.fnb .track span{height:100%;border-radius:3px;min-width:0}

/* ── Flow chart (Sankey) and role cards ───────────────── */
.sankey .lnk{fill:none;stroke-opacity:.28;transition:stroke-opacity .25s ease,opacity .9s ease var(--d,0s)}
.sankey .lnk:hover,.sankey .lnk:focus,.sankey .lnk.hl{stroke-opacity:.6;outline:none}
.sankey.dim .lnk:not(.hl){stroke-opacity:.08}
.motion .tab-pnl:not(.in) .sankey .lnk{opacity:0}
.sankey .node{transition:transform .8s var(--ease) var(--d,0s);transform-box:fill-box;transform-origin:50% 50%}
.motion .tab-pnl:not(.in) .sankey .node{transform:scaleY(0)}
.sankey .nl{font-size:12px;fill:var(--ink);font-weight:500}
.sankey .nv{font-size:11px;fill:var(--muted)}
.sankey .nd{cursor:pointer;outline:none;transition:opacity .25s ease}
.sankey.dim .nd:not(.hl){opacity:.45}
.sankey .nd:focus-visible .node,.sankey .nd.sel .node{stroke:var(--ink);stroke-width:2}
.sankey .nd.sel .nl{font-weight:700}
.sankey-head{display:flex;justify-content:space-between;font-size:10.5px;letter-spacing:.14em;text-transform:uppercase;color:var(--muted);font-weight:600;margin:0 0 6px}
/* Role Evolution (WI 2884): the Sankey, its totals and the chosen role's card */
.evo-tot{margin-top:10px}
/* Grouped by owner (WI 2921): the all-roles toggle, an open owner's header, the owner card's sources */
.evo-bar{display:flex;justify-content:flex-end;margin:0 0 6px}
.evo-all{border:1px solid var(--line);background:var(--surface);border-radius:9999px;padding:4px 12px;font-size:11.5px;font-weight:500;color:var(--ink2);cursor:pointer}
.evo-all:hover,.evo-all[aria-pressed="true"]{border-color:var(--brand);color:var(--brand-ink)}
.evo-all:focus-visible{outline:2px solid var(--brand);outline-offset:2px}
.sankey .nh{font-size:11px;fill:var(--muted);font-weight:600;letter-spacing:.04em}
.sankey .grp-h:hover .nh,.sankey .grp-h:focus-visible .nh,.sankey .grp-h.sel .nh{fill:var(--ink);text-decoration:underline}
.sankey .ex{fill:var(--muted);font-size:11px}
.evo-src{display:block;color:var(--muted);font-size:11px;margin-top:1px}
.evo-fb,.evo-excl{margin-top:4px}
.evo-card{margin-top:16px}
.evo-links{list-style:none;margin:0;padding:0;display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:4px 20px;font-size:12.5px}
.evo-links li{display:flex;justify-content:space-between;gap:10px;border-bottom:1px solid var(--line2);padding:4px 0}
.evo-skills{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:16px;font-size:12px}
.rev{background:var(--surface);border:1px solid var(--line);border-radius:var(--r-lg);box-shadow:var(--sh-1);padding:20px 22px;display:flex;flex-direction:column;gap:12px}
.rev-top{display:flex;justify-content:space-between;align-items:flex-start;gap:10px}
.rev-name{font-size:15px;font-weight:600;letter-spacing:-.01em}
.rev-hc{font-size:12px;color:var(--muted);margin-top:2px}
.rev-k{font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:var(--muted);font-weight:600;margin-bottom:6px}
.rev-chips{display:flex;flex-wrap:wrap;gap:5px}
.rev-chips span{padding:3px 9px;border-radius:999px;background:var(--line2);color:var(--ink2);font-size:11.5px}
.rev-chips.new span{background:var(--brand-soft);color:var(--brand-ink)}
/* Agent Marketplace cards (WI 2822) */
.card-list{list-style:none;margin:0;padding:0}
.build-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:16px}
.build{background:var(--surface);border:1px solid var(--line);border-top:3px solid var(--bc,var(--brand));border-radius:var(--r-lg);box-shadow:var(--sh-1);padding:16px 18px;display:flex;flex-direction:column;gap:8px}
.build-top{display:flex;flex-wrap:wrap;gap:6px}
.build-name{font-size:14px;font-weight:600;letter-spacing:-.01em;line-height:1.35}
.build-cap{font-size:12px;color:var(--muted)}
.build-figs{display:flex;gap:22px;margin-top:auto;padding-top:8px;border-top:1px solid var(--line2)}
.build-figs .v{font-size:16px;font-weight:600;letter-spacing:-.01em}
.build-figs .l{font-size:11px;color:var(--muted);margin-top:1px}
/* Work Landscape (WI 2882): metric strip, breadcrumb, lens switch, drill-down cards and task rows */
.wv-metrics .unlock{grid-template-columns:repeat(auto-fit,minmax(150px,1fr))}
.wv-excl{margin-top:12px;color:#8A5A00}
.wv-crumbs ol{list-style:none;margin:0 0 12px;padding:0;display:flex;flex-wrap:wrap;align-items:center;gap:4px;font-size:13px}
.wv-crumbs li+li::before{content:'›';color:var(--muted);margin:0 6px}
.wv-crumbs button{border:none;background:none;padding:2px 4px;font:inherit;color:var(--brand-ink);cursor:pointer;text-decoration:underline;text-underline-offset:3px}
.wv-crumbs .wv-here{font-weight:600;padding:2px 4px;outline:none}
.wv-head{margin:16px 0 12px}
.wv-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(250px,1fr));gap:16px}
.wv-card{width:100%;text-align:left;font:inherit;color:inherit;cursor:pointer;background:var(--surface);border:1px solid var(--line);border-radius:var(--r-lg);box-shadow:var(--sh-1);padding:16px 18px;display:flex;flex-direction:column;gap:8px}
.wv-card:focus-visible{outline:2px solid var(--brand);outline-offset:2px}
.wv-name{font-size:14px;font-weight:600;letter-spacing:-.01em;line-height:1.35}
.wv-sub{font-size:12px;color:var(--muted)}
.wv-figs{display:flex;flex-wrap:wrap;gap:4px 14px;font-size:11.5px;color:var(--muted)}
.wv-figs b{font-size:15px;font-weight:600;color:var(--ink);letter-spacing:-.01em}
.wv-bar{display:flex;height:8px;border-radius:4px;overflow:hidden;background:var(--line2);margin-top:4px}
.wv-bar span{display:block;height:100%}
.wv-roles{display:grid;grid-template-columns:repeat(auto-fill,minmax(420px,1fr));gap:16px}
.wv-role{padding:16px 18px}
.wv-role-h{display:flex;flex-direction:column;gap:2px;margin-bottom:10px}
.wv-tasks{display:flex;flex-direction:column}
.wv-task{display:grid;grid-template-columns:10px minmax(0,1fr) auto;gap:4px 10px;align-items:baseline;padding:8px 0;border-top:1px solid var(--line2);font-size:12.5px}
.wv-dot{width:10px;height:10px;border-radius:50%;background:var(--wc,var(--muted));align-self:center}
.wv-tn{font-weight:500}
.wv-tf{font-variant-numeric:tabular-nums;color:var(--ink2);white-space:nowrap}
.wv-tb{grid-column:2/4;display:flex;flex-wrap:wrap;gap:4px}
.wv-sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
/* Process View (WI 2885): a section per function, a card per Step 0 process with its task table */
.pv-excl{margin:0 0 12px;color:#8A5A00}
.pv-head{margin-bottom:12px}
.pv-proc{margin-bottom:16px}
.pv-proc-h{display:flex;flex-wrap:wrap;align-items:baseline;justify-content:space-between;gap:4px 12px;margin-bottom:8px}
.pv-n{color:var(--muted)}
/* Fixed columns, so every process's table lines up with the next */
.pv-proc table{table-layout:fixed;min-width:900px}
.pv-proc th:nth-child(1){width:48px}
.pv-proc th:nth-child(2){width:22%}
.pv-proc th:nth-child(3){width:14%}
.pv-proc th:nth-child(4){width:11%}
.pv-proc th:nth-child(5){width:10%}
.pv-proc td{overflow-wrap:anywhere}

/* ── Scenario explorer ────────────────────────────────── */
.sc{display:grid;grid-template-columns:320px minmax(0,1fr) 300px;gap:20px;align-items:start}
.ctl{display:flex;flex-direction:column;gap:22px}
.ctl label{display:block;font-size:13px;font-weight:600}
.ctl .hint{font-size:11.5px;color:var(--muted);margin:2px 0 10px;line-height:1.45}
.ctl-row{display:flex;align-items:center;gap:12px}
.ctl-row output{min-width:52px;text-align:center;font-size:12.5px;font-weight:600;padding:4px 8px;border-radius:8px;border:1px solid var(--line);font-variant-numeric:tabular-nums}
input[type=range]{-webkit-appearance:none;appearance:none;flex:1;height:4px;border-radius:4px;background:linear-gradient(90deg,var(--brand) 0%,var(--brand) var(--p,100%),var(--line) var(--p,100%));outline:none;cursor:pointer}
input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:18px;height:18px;border-radius:50%;background:#fff;border:2px solid var(--brand);box-shadow:0 2px 6px rgba(14,16,51,.2);transition:transform .15s ease}
input[type=range]::-webkit-slider-thumb:hover{transform:scale(1.15)}
input[type=range]::-moz-range-thumb{width:16px;height:16px;border-radius:50%;background:#fff;border:2px solid var(--brand)}
input[type=range]:focus-visible{outline:2px solid var(--brand);outline-offset:6px}
.btn-ghost{align-self:flex-start;border:1px solid var(--line);background:var(--surface);border-radius:10px;padding:8px 14px;font-size:12.5px;font-weight:600;color:var(--ink2);cursor:pointer;transition:border-color .2s,color .2s}
.btn-ghost:hover{border-color:var(--brand);color:var(--ink)}
.stack-cols{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:28px;align-items:end;height:300px;padding:0 10px;border-bottom:1px solid var(--line)}
.scol{display:flex;flex-direction:column;align-items:center;height:100%;justify-content:flex-end}
.scol .tot{font-size:13px;font-weight:600;margin-bottom:6px;font-variant-numeric:tabular-nums}
.scol .stk{width:min(96px,70%);display:flex;flex-direction:column-reverse;gap:2px}
.scol .stk span{display:block;border-radius:4px;transition:height .6s var(--ease)}
.scol-lbls{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:28px;padding:8px 10px 0;text-align:center;font-size:12px;color:var(--ink2);font-weight:500}
.scol-lbls small{display:block;color:var(--muted);font-weight:400;font-size:11px}
.ko{display:flex;flex-direction:column}
.ko>div{padding:14px 0;border-top:1px solid var(--line2)}
.ko>div:first-of-type{border-top:none;padding-top:4px}
.ko .v{font-size:24px;font-weight:600;letter-spacing:-.02em;line-height:1.1}
.ko .l{font-size:12px;color:var(--ink2);margin-top:3px}
.ko .s{font-size:11px;color:var(--muted)}

/* ── Detail tables ────────────────────────────────────── */
.filters{display:flex;flex-wrap:wrap;align-items:center;gap:6px;margin-bottom:14px;padding:12px 14px;background:var(--surface);border:1px solid var(--line);border-radius:var(--r-md)}
.f-lbl{font-size:10.5px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);margin:0 2px 0 12px}
.f-lbl:first-child{margin-left:0}
.pill{border:1px solid var(--line);background:var(--surface);border-radius:9999px;padding:4px 11px;font-size:11.5px;font-weight:500;color:var(--ink2);cursor:pointer;transition:all .15s ease}
.pill:not(.active):hover{border-color:var(--brand);color:var(--ink)}
.pill.active{background:var(--ink);border-color:var(--ink);color:#fff}
.tbl-wrap{overflow-x:auto;border-radius:var(--r-md);border:1px solid var(--line);background:var(--surface);box-shadow:var(--sh-1)}
table{width:100%;border-collapse:collapse;font-size:13px}
thead{background:#FAFAFD}
th{padding:11px 14px;text-align:left;font-size:10.5px;font-weight:600;text-transform:uppercase;letter-spacing:.08em;color:var(--muted);border-bottom:1px solid var(--line);white-space:nowrap}
td{padding:11px 14px;border-bottom:1px solid var(--line2);vertical-align:top;font-variant-numeric:tabular-nums}
tr:last-child td{border-bottom:none}
tr.hidden,[data-card].hidden{display:none}
tbody tr{transition:background .15s ease}
tbody tr:hover td{background:#FAF7FF}
.badge{display:inline-block;padding:2px 9px;border-radius:9999px;font-size:11px;font-weight:600;white-space:nowrap}
.b-green{background:#E3F6EC;color:#0F6B45}
.b-blue{background:#E6EEFF;color:#2A3BA8}
.b-purple{background:#F3E6FF;color:#6A00A8}
.b-yellow{background:#FFF4D6;color:#8A5A00}
.b-orange{background:#FDEBDD;color:#9A4A12}
.b-red{background:#FDE7E5;color:#A3211A}
.b-gray{background:#EEF0F5;color:#4A4E66}
.b-cyan{background:#E1F3FB;color:#0B6690}
details{font-size:12px}
details summary{color:var(--muted);cursor:pointer;list-style:none;-webkit-user-select:none;user-select:none}
details summary::-webkit-details-marker{display:none}
details summary:hover{color:var(--ink)}
details[open] summary{color:var(--ink)}
details .rat-body{color:var(--ink2);line-height:1.55;max-width:420px;padding-top:4px}
.dot{display:inline-block;width:8px;height:8px;border-radius:50%;margin:0 1px}

/* ── Tooltip, footer ──────────────────────────────────── */
.tt{position:fixed;z-index:50;pointer-events:none;background:rgba(14,16,51,.96);color:#fff;border-radius:10px;padding:9px 12px;font-size:12px;line-height:1.5;box-shadow:0 10px 30px rgba(14,16,51,.25);opacity:0;transform:translateY(4px);transition:opacity .15s ease,transform .15s ease;max-width:280px}
.tt.on{opacity:1;transform:none}
.tt b{font-weight:600}
th[data-tip],td[data-tip]{cursor:help;text-decoration:underline dotted;text-underline-offset:3px}
th[data-tip]:focus-visible,td[data-tip]:focus-visible{outline:2px solid var(--brand);outline-offset:-2px}
.footer{padding:18px 40px;display:flex;justify-content:space-between;gap:16px;flex-wrap:wrap;font-size:11.5px;color:var(--muted);border-top:1px solid var(--line);background:var(--surface)}

/* ── Keyframes ────────────────────────────────────────── */
@keyframes heroIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
@keyframes drift1{from{transform:translate(0,0) scale(1)}to{transform:translate(-120px,60px) scale(1.15)}}
@keyframes drift2{from{transform:translate(0,0) scale(1)}to{transform:translate(160px,-40px) scale(.9)}}

/* ── Responsive ───────────────────────────────────────── */
@media (max-width:1200px){
  .sc{grid-template-columns:1fr 1fr}
  .sc .ko-card{grid-column:1 / -1}
  .step-list{grid-template-columns:1fr 1fr;gap:20px}
  .step-list::before{display:none}
  .intro-cards{grid-template-columns:1fr 1fr}
}
@media (max-width:1000px){
  .hero,.tab-nav,.tab-pnl,.footer{padding-left:20px;padding-right:20px}
  .hero-body{grid-template-columns:1fr}
  .hero-headline{text-align:left;border-left:none;padding-left:0}
  .hero-headline .hh-lbl{margin-left:0}
  .tagline{display:none}
  .hero-title{font-size:32px}
  .states{grid-auto-flow:row}
  .state+.state::after{display:none}
  .grid2,.grid3,.vc-cards,.levers,.evo-skills,.intro-cards,.sc{grid-template-columns:1fr}
  .lever{border-left:none;border-top:1px solid var(--line2)}
}

/* ── Reduced motion & print ───────────────────────────── */
@media (prefers-reduced-motion:reduce){
  *,*::before,*::after{animation:none!important;transition:none!important}
}
@media print{
  @page{size:A4 landscape;margin:10mm}
  body{background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  *,*::before,*::after{animation:none!important;transition:none!important}
  .tab-nav{display:none}
  .tab-pnl{display:block!important;break-before:page;padding:16px 0}
  .tab-pnl:first-of-type{break-before:auto}
  .tab-pnl[data-title]::before{content:attr(data-title);display:block;font-size:20px;font-weight:600;margin-bottom:14px}
  .rv,.fade,.sankey .lnk{opacity:1!important;transform:none!important}
  .bar,.hbar,.sankey .node{transform:none!important}
  .card,.state,.vc-card,.lever,.rev,.tbl-wrap{box-shadow:none;break-inside:avoid}
  .tt{display:none}
}
`
